import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { fmtDate } from "@/lib/format";
import { eur } from "@/components/enrollment/FormParts";
import { EnrollmentDetailDialog } from "@/components/enrollment/EnrollmentDetailDialog";
import { EnrollmentSettingsDialog } from "@/components/enrollment/EnrollmentSettingsDialog";
import { Link2, Settings, ExternalLink } from "lucide-react";

const STATUS = { in_attesa: ["In attesa", "bg-amber-50 text-amber-800 border-amber-200"], approvata: ["Approvata", "bg-emerald-50 text-emerald-800 border-emerald-200"], rifiutata: ["Rifiutata", "bg-stone-100 text-stone-600 border-stone-200"] };

export default function Iscrizioni() {
  const [status, setStatus] = useState("in_attesa");
  const [sel, setSel] = useState(null);
  const [settings, setSettings] = useState(false);
  const { data = [], isLoading } = useQuery({
    queryKey: ["enrollments", status],
    queryFn: async () => (await api.get("/enrollments", { params: status ? { status } : {} })).data,
  });
  const link = `${window.location.origin}/iscrizione-modulare`;
  const copy = () => navigator.clipboard.writeText(link).then(() => toast.success("Link copiato"));

  return (
    <div className="p-4 sm:p-6 md:p-10 max-w-[1300px] mx-auto" data-testid="enrollments-page">
      <PageHeader title="Pre-iscrizioni · Scuola Modulare" subtitle="Moduli inviati dal link pubblico. Controlla i dati e approva per creare lo studente modulare."
        actions={<div className="flex gap-2 flex-wrap">
          <Button variant="outline" className="rounded-full" onClick={copy} data-testid="enr-copy-link"><Link2 size={14} className="mr-1.5"/> Copia link modulo</Button>
          <Button variant="outline" className="rounded-full" asChild><a href="/iscrizione-modulare" target="_blank" rel="noreferrer" data-testid="enr-open-form"><ExternalLink size={14} className="mr-1.5"/> Apri modulo</a></Button>
          <Button variant="outline" className="rounded-full" onClick={() => setSettings(true)} data-testid="enr-settings-btn"><Settings size={14} className="mr-1.5"/> Impostazioni e IBAN</Button>
        </div>}/>
      <div className="flex gap-2 mb-4">
        {[["in_attesa", "In attesa"], ["approvata", "Approvate"], ["rifiutata", "Rifiutate"], ["", "Tutte"]].map(([v, l]) => (
          <button key={l} onClick={() => setStatus(v)} data-testid={`enr-filter-${v || "all"}`}
            className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${status === v ? "bg-[#1C352D] text-white border-[#1C352D]" : "bg-white border-stone-200 text-stone-600"}`}>{l}</button>
        ))}
      </div>
      <Card className="border border-stone-200 rounded-xl shadow-none divide-y divide-stone-100">
        {isLoading && <div className="p-8 text-center text-sm text-stone-400">Caricamento…</div>}
        {!isLoading && !data.length && <div className="p-8 text-center text-sm text-stone-400" data-testid="enr-empty">Nessuna iscrizione.</div>}
        {data.map((e) => (
          <button key={e.id} onClick={() => setSel(e)} className="w-full text-left px-4 py-3 hover:bg-stone-50 flex flex-wrap items-center gap-x-4 gap-y-1" data-testid={`enr-row-${e.id}`}>
            <span className="font-medium text-[#1C352D] min-w-[180px]">{e.last_name} {e.first_name}</span>
            <span className="text-xs text-stone-500">{e.email} · {e.phone}</span>
            <span className="text-xs text-stone-600">{e.payment_plan === "unica" ? "Unica" : e.payment_plan === "mensile" ? "Mensile" : `${e.amount?.installments} rate`} · {eur(e.amount?.total)}{e.couple ? " · in coppia" : ""}</span>
            <span className="text-xs text-stone-400">{(e.files || []).length} allegati</span>
            <span className="ml-auto flex items-center gap-2">
              <span className="text-xs text-stone-400">{fmtDate(e.created_at)}</span>
              <Badge className={`text-[10px] rounded-full border ${STATUS[e.status]?.[1]}`}>{STATUS[e.status]?.[0]}</Badge>
            </span>
          </button>
        ))}
      </Card>
      <EnrollmentDetailDialog e={sel} onClose={() => setSel(null)}/>
      <EnrollmentSettingsDialog open={settings} onOpenChange={setSettings}/>
    </div>
  );
}
